import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { DataGridPremiumProps, GridCellParams } from "@mui/x-data-grid-premium";
import { ReactNode, useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { Fetching } from "components/common/loading/fetching";
import { buildViewLabelsUrl } from "components/labellerAccuracy/utils";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  DailyLabellerStatsEntry,
  DailySessionDetail,
  LabellerDetails,
} from "src/store/api/generatedApi";
import {
  buildDailyActivityColumns,
  buildDailyActivitySessionCsvColumns,
  DailyActivityRow,
  SessionCsvRow,
} from "./dailyActivityColumns";
import { DailySessionPanel, SessionWithTenant } from "./dailySessionPanel";
import {
  DAILY_ACTIVITY_METRICS,
  DailyActivityChart,
  DailyActivityMetric,
  LABELLING_PALETTE_KEY,
  REVIEWING_PALETTE_KEY,
} from "./DailyActivityChart";
import { DownloadStatsCsvButton } from "./downloadStatsCsvButton";

export type DailyStatsForTenant = Readonly<{
  tenantName: string;
  dailyStats: readonly DailyLabellerStatsEntry[];
}>;

const LOADING_OVERLAY_SLOT_PROPS = {
  loadingOverlay: {
    variant: "skeleton",
    noRowsVariant: "skeleton",
  },
} as const;

const GET_DETAIL_PANEL_HEIGHT = () => "auto" as const;

const enrichSessionsWithTenant = (
  tenantName: string,
  sessions: readonly DailySessionDetail[]
): SessionWithTenant[] =>
  sessions.map((session) => ({ ...session, tenantName }));

const accumulateEntry = (
  accumulator: Map<string, DailyActivityRow>,
  tenantName: string,
  entry: DailyLabellerStatsEntry
): Map<string, DailyActivityRow> => {
  const enrichedSessions = enrichSessionsWithTenant(tenantName, entry.sessions);
  const existing = accumulator.get(entry.date);
  if (existing) {
    return accumulator.set(entry.date, {
      ...existing,
      time_labelled_seconds:
        existing.time_labelled_seconds + entry.time_labelled_seconds,
      time_spent_labelling_seconds:
        existing.time_spent_labelling_seconds +
        entry.time_spent_labelling_seconds,
      submissions: existing.submissions + entry.submissions,
      sessions: [...existing.sessions, ...enrichedSessions],
    });
  }
  return accumulator.set(entry.date, {
    ...entry,
    id: entry.date,
    sessions: enrichedSessions,
  });
};

export const aggregateDailyActivityRows = (
  labellingByTenant: readonly DailyStatsForTenant[],
  reviewingByTenant: readonly DailyStatsForTenant[]
): DailyActivityRow[] => {
  const accumulator = new Map<string, DailyActivityRow>();
  for (const { tenantName, dailyStats } of labellingByTenant) {
    for (const entry of dailyStats) {
      accumulateEntry(accumulator, tenantName, entry);
    }
  }
  for (const { tenantName, dailyStats } of reviewingByTenant) {
    for (const entry of dailyStats) {
      accumulateEntry(accumulator, tenantName, entry);
    }
  }
  return [...accumulator.values()];
};

export const useDailyActivityViewState = () => {
  const [showLabelling, setShowLabelling] = useState(true);
  const [showReviewing, setShowReviewing] = useState(true);
  const [selectedMetric, setSelectedMetric] =
    useState<DailyActivityMetric>("totalTime");

  return {
    showLabelling,
    setShowLabelling,
    showReviewing,
    setShowReviewing,
    selectedMetric,
    setSelectedMetric,
  };
};

type DailyActivityViewProps<TLabeller extends LabellerDetails> = Readonly<{
  labellers: readonly TLabeller[];
  labellersLoading: boolean;
  selectedLabeller: TLabeller | null;
  onSelectLabeller: (labeller: TLabeller | null) => void;
  labellingByTenant: readonly DailyStatsForTenant[];
  reviewingByTenant: readonly DailyStatsForTenant[];
  isLoading: boolean;
  isFetching: boolean;
  state: ReturnType<typeof useDailyActivityViewState>;
  toolbarLeading?: ReactNode;
}>;

export const DailyActivityView = <TLabeller extends LabellerDetails>({
  labellers,
  labellersLoading,
  selectedLabeller,
  onSelectLabeller,
  labellingByTenant,
  reviewingByTenant,
  isLoading,
  isFetching,
  state,
  toolbarLeading,
}: DailyActivityViewProps<TLabeller>) => {
  const { t } = useTenantTranslation();
  const formatNumber1dp = useFormatNumber()(1);
  const theme = useTheme();
  const labellingSwatchColor = theme.palette.data[LABELLING_PALETTE_KEY];
  const reviewingSwatchColor = theme.palette.data[REVIEWING_PALETTE_KEY];

  const {
    showLabelling,
    setShowLabelling,
    showReviewing,
    setShowReviewing,
    selectedMetric,
    setSelectedMetric,
  } = state;

  // Aggregated, not flattened: the chart must plot the grid's per-date grain,
  // or a ratio metric shows un-poolable per-tenant values beside a pooled one.
  const labellingStats = useMemo<readonly DailyLabellerStatsEntry[]>(
    () =>
      isFetching || !showLabelling
        ? []
        : aggregateDailyActivityRows(labellingByTenant, []),
    [labellingByTenant, showLabelling, isFetching]
  );
  const reviewingStats = useMemo<readonly DailyLabellerStatsEntry[]>(
    () =>
      isFetching || !showReviewing
        ? []
        : aggregateDailyActivityRows([], reviewingByTenant),
    [reviewingByTenant, showReviewing, isFetching]
  );

  const rows = useMemo(
    () =>
      aggregateDailyActivityRows(
        showLabelling && !isFetching ? labellingByTenant : [],
        showReviewing && !isFetching ? reviewingByTenant : []
      ),
    [
      labellingByTenant,
      reviewingByTenant,
      showLabelling,
      showReviewing,
      isFetching,
    ]
  );

  const totalRow: DailyActivityRow = useMemo(
    () => ({
      id: "TOTAL",
      date: t("total"),
      time_labelled_seconds: rows.reduce(
        (sum, row) => sum + row.time_labelled_seconds,
        0
      ),
      time_spent_labelling_seconds: rows.reduce(
        (sum, row) => sum + row.time_spent_labelling_seconds,
        0
      ),
      submissions: rows.reduce((sum, row) => sum + row.submissions, 0),
      sessions: [],
    }),
    [rows, t]
  );

  const pinnedRows = useMemo(() => ({ bottom: [totalRow] }), [totalRow]);

  const initialState = useMemo(
    () => ({
      sorting: { sortModel: [{ field: "date", sort: "desc" as const }] },
      pagination: { paginationModel: { pageSize: 20 } },
    }),
    []
  );

  const dataGridSx = useMemo(
    () => ({
      flex: 1,
      minWidth: 0,
      height: 1000,
      "& .total-row": { fontWeight: 700 },
    }),
    []
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<DailyActivityRow>) =>
      params.row.id === "TOTAL" ? "total-row" : "",
    []
  );

  const getDetailPanelContent = useMemo<
    DataGridPremiumProps<DailyActivityRow>["getDetailPanelContent"]
  >(
    () =>
      ({ row }) =>
        row.id === "TOTAL" || row.sessions.length === 0 ? null : (
          <DailySessionPanel
            sessions={row.sessions}
            labeller={selectedLabeller}
          />
        ),
    [selectedLabeller]
  );

  const labellerOptions = useMemo<readonly TLabeller[]>(() => {
    if (
      selectedLabeller === null ||
      labellers.some((option) => option.id === selectedLabeller.id)
    ) {
      return labellers;
    }
    return [selectedLabeller, ...labellers];
  }, [labellers, selectedLabeller]);

  const csvSessionRows = useMemo<SessionCsvRow[]>(() => {
    if (!selectedLabeller) return [];
    const labellerName = selectedLabeller.name ?? selectedLabeller.id;
    return rows.flatMap((row) =>
      row.sessions.map((session) => ({
        ...session,
        date_labelled: row.date,
        labeller_name: labellerName,
        url:
          window.location.origin +
          buildViewLabelsUrl({
            tenantName: session.tenantName,
            location: session.location_name,
            labeller: selectedLabeller,
            labellerEventStart: session.footage_start_at,
          }),
      }))
    );
  }, [rows, selectedLabeller]);

  const csvColumns = useMemo(() => buildDailyActivitySessionCsvColumns(t), [t]);

  const buildCsvFilename = useCallback(() => {
    const labellerSlug = (selectedLabeller?.name ?? "labeller")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    return `daily-activity-${labellerSlug}-${dayjs().format("YYYY-MM-DD")}.csv`;
  }, [selectedLabeller]);

  const columns = useMemo(
    () => buildDailyActivityColumns(t, formatNumber1dp),
    [t, formatNumber1dp]
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        useFlexGap
        sx={{ pt: 1, flexWrap: "wrap" }}
      >
        {toolbarLeading}
        {labellersLoading || labellerOptions.length > 0 ? (
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={labellerOptions}
            loading={labellersLoading}
            getOptionLabel={(option) =>
              option.name ?? `${option.id} (${option.labeller_type})`
            }
            value={selectedLabeller}
            onChange={(_, newValue) => {
              onSelectLabeller(newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t("selectLabeller")} />
            )}
            isOptionEqualToValue={(option, value) =>
              option.id === value.id &&
              option.labeller_type === value.labeller_type
            }
          />
        ) : null}
        <FormControlLabel
          control={
            <Checkbox
              checked={showLabelling}
              onChange={(e) => {
                setShowLabelling(e.target.checked);
              }}
              size="small"
            />
          }
          label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  backgroundColor: labellingSwatchColor,
                }}
              />
              <Box component="span">{t("label")}</Box>
            </Stack>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showReviewing}
              onChange={(e) => {
                setShowReviewing(e.target.checked);
              }}
              size="small"
            />
          }
          label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  backgroundColor: reviewingSwatchColor,
                }}
              />
              <Box component="span">{t("review")}</Box>
            </Stack>
          }
        />
        <Box sx={{ flex: 1 }} />
        <Select
          size="small"
          value={selectedMetric}
          onChange={(e: SelectChangeEvent) => {
            setSelectedMetric(e.target.value as DailyActivityMetric);
          }}
          sx={{ minWidth: 200 }}
        >
          {DAILY_ACTIVITY_METRICS.map((metric) => (
            <MenuItem key={metric} value={metric}>
              {t(metric)}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Stack direction="row" justifyContent="flex-end">
        <DownloadStatsCsvButton
          rows={csvSessionRows}
          columns={csvColumns}
          filename={buildCsvFilename}
        />
      </Stack>
      <Fetching fetching={isFetching}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "stretch" }}>
          <HotkeyCompliantDataGrid
            columns={columns}
            rows={rows}
            pinnedRows={pinnedRows}
            getCellClassName={getCellClassName}
            loading={isLoading}
            pagination
            hideFooter={false}
            pageSizeOptions={[20]}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={GET_DETAIL_PANEL_HEIGHT}
            initialState={initialState}
            slotProps={LOADING_OVERLAY_SLOT_PROPS}
            rowHeight={40}
            columnHeaderHeight={44}
            disableRowSelectionOnClick
            sx={dataGridSx}
          />
          <DailyActivityChart
            labellingStats={labellingStats}
            reviewingStats={reviewingStats}
            selectedMetric={selectedMetric}
          />
        </Stack>
      </Fetching>
    </Stack>
  );
};
